import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Input, Dropdown } from '../../../../../common/ui';
import { createUser, findByUsername } from '../../actions';
import { showAlert } from '../../../../../common/actions';
import styles from './styles.module.css';

const AddNewUser = memo(({
  createUser,
  findByUsername,
  showAlert,
  auth: { companyId, branchId, branchList },
}) => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBranchDisabled, setBranchDisabled] = useState(false);

  const [isErrorRole, setIsErrorRole] = useState(false);
  const [isErrorBranch, setIsErrorBranch] = useState(false);
  const [isErrorName, setIsErrorName] = useState(false);
  const [isErrorAge, setIsErrorAge] = useState(false);
  const [isErrorGender, setIsErrorGender] = useState(false);
  const [isErrorAddress, setIsErrorAddress] = useState(false);
  const [isErrorEmail, setIsErrorEmail] = useState(false);
  const [isErrorPhone, setIsErrorPhone] = useState(false);
  const [isErrorUserName, setIsErrorUserName] = useState(false);
  const [isErrorPassword, setIsErrorPassword] = useState(false);
  const [isErrorPin, setIsErrorPin] = useState(false);

  const [roleErrorMessage, setRoleErrorMessage] = useState('');
  const [branchErrorMessage, setBranchErrorMessage] = useState('');
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  const [ageErrorMessage, setAgeErrorMessage] = useState('');
  const [genderErrorMessage, setGenderErrorMessage] = useState('');
  const [addressErrorMessage, setAddressErrorMessage] = useState('');
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [phoneErrorMessage, setPhoneErrorMessage] = useState('');
  const [userNameErrorMessage, setUserNameErrorMessage] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [pinErrorMessage, setPinErrorMessage] = useState('');

  const [newUser, setNewUser] = useState({
    name: '',
    age: '',
    gender: '',
    email: '',
    phone: '',
    address: '',
    username: '',
    password: '',
    pin: '',
    role: 'cashier',
    branchId: branchId || '',
  });

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'cashier', label: 'Cashier' },
    { value: 'branchManager', label: 'Branch Manager' },
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
  ];

  useEffect(() => {
    if (newUser.role === 'admin') {
      setBranchDisabled(true);
      setNewUser((prev) => ({
        ...prev,
        branchId: '',
      }));
    } else {
      setBranchDisabled(false);
      if (branchId) {
        setNewUser((prev) => ({
          ...prev,
          branchId: branchId,
        }));
      }
    }
  }, [newUser.role, branchId]);

  const validations = async () => {
    let isValid = true;

    if (!newUser.branchId && newUser.role !== 'admin') {
      isValid = false;
      setIsErrorBranch(true);
      setBranchErrorMessage('Branch is required');
    }

    if (!newUser.name) {
      isValid = false;
      setIsErrorName(true);
      setNameErrorMessage('Name is required');
    }

    if (!newUser.gender) {
      isValid = false;
      setIsErrorGender(true);
      setGenderErrorMessage('Gender is required');
    }

    if (!newUser.phone) {
      isValid = false;
      setIsErrorPhone(true);
      setPhoneErrorMessage('Phone number is required');
    }

    if (!newUser.username) {
      isValid = false;
      setIsErrorUserName(true);
      setUserNameErrorMessage('Username is required');
    } else {
      const isUsernameExists = await findByUsername(newUser.username);
      if (isUsernameExists) {
        isValid = false;
        setIsErrorUserName(true);
        setUserNameErrorMessage('Username already exists');
      } else {
        setIsErrorUserName(false);
        setUserNameErrorMessage('');
      }
    }

    if (!newUser.password) {
      isValid = false;
      setIsErrorPassword(true);
      setPasswordErrorMessage('Password is required');
    }

    if (!/^\d{6}$/.test(newUser.pin)) {
      isValid = false;
      setIsErrorPin(true);
      setPinErrorMessage('PIN must be a 6-digit number');
    }

    return isValid;
  };

  const handleChange = async (e) => {
    const { id, value } = e.target;

    if (id === 'name') {
      setIsErrorName(false);
      setNameErrorMessage('');
    }
    if (id === 'age') {
      if (!/^\d{0,2}$/.test(value)) {
        setIsErrorAge(true);
        setAgeErrorMessage('Age must be a number with max 2 digits');
        return;
      }
      setIsErrorAge(false);
      setAgeErrorMessage('');
    }
    if (id === 'address') {
      setIsErrorAddress(false);
      setAddressErrorMessage('');
    }
    if (id === 'email') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setIsErrorEmail(true);
        setEmailErrorMessage('Please enter a valid email address');
      } else {
        setIsErrorEmail(false);
        setEmailErrorMessage('');
      }
    }
    if (id === 'phone') {
      if (!/^\d*$/.test(value)) {
        setIsErrorPhone(true);
        setPhoneErrorMessage('Phone number must contain only numbers');
        return;
      }
      setIsErrorPhone(false);
      setPhoneErrorMessage('');
    }
    if (id === 'username') {
      setIsErrorUserName(false);
      setUserNameErrorMessage('');
    }
    if (id === 'password') {
      setIsErrorPassword(false);
      setPasswordErrorMessage('');
    }
    if (id === 'pin') {
      if (!/^\d{0,6}$/.test(value)) {
        setIsErrorPin(true);
        setPinErrorMessage('PIN must be a 6-digit number');
        return;
      }
      setIsErrorPin(false);
      setPinErrorMessage('');
    }

    setNewUser((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;

    if (name === 'role') {
      setIsErrorRole(false);
      setRoleErrorMessage('');
    }
    if (name === 'branchId') {
      setIsErrorBranch(false);
      setBranchErrorMessage('');
    }
    if (name === 'gender') {
      setIsErrorGender(false);
      setGenderErrorMessage('');
    }

    setNewUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const isValid = await validations();

    if (isValid) {
      setIsSubmitting(true);
      const user = {
        role: newUser.role,
        branchId: newUser.branchId,
        name: newUser.name,
        age: newUser.age || null,
        gender: newUser.gender,
        address: newUser.address,
        email: newUser.email,
        phone: newUser.phone,
        username: newUser.username,
        password: newUser.password,
        pin: newUser.pin,
      };

      const requestBody = {
        user,
      };

      const isSuccess = await createUser(requestBody);

      if (isSuccess) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'New User created successfully',
        });
        navigate('/iam/users');
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Something went wrong. Please Try Again.',
        });
      }
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setNewUser({
      name: '',
      age: '',
      gender: '',
      email: '',
      phone: '',
      address: '',
      username: '',
      password: '',
      pin: '',
      role: 'cashier',
      branchId: branchId || '',
    });
    setIsErrorRole(false);
    setIsErrorBranch(false);
    setIsErrorName(false);
    setIsErrorAge(false);
    setIsErrorGender(false);
    setIsErrorAddress(false);
    setIsErrorEmail(false);
    setIsErrorPhone(false);
    setIsErrorUserName(false);
    setIsErrorPassword(false);
    setIsErrorPin(false);
    setRoleErrorMessage('');
    setBranchErrorMessage('');
    setNameErrorMessage('');
    setAgeErrorMessage('');
    setGenderErrorMessage('');
    setAddressErrorMessage('');
    setEmailErrorMessage('');
    setPhoneErrorMessage('');
    setUserNameErrorMessage('');
    setPasswordErrorMessage('');
    setPinErrorMessage('');
  };

  const handleCancel = () => {
    navigate('/iam/users');
  };

  const branchOptions = (branchList || []).map((branch) => ({
    value: branch.id,
    label: branch.name || branch.branchName,
  }));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Create New User</h1>
        <p className={styles.caption}>Add a new user for your store</p>
      </div>
      <div className={styles.formCard}>
        <div className={styles.roleSection}>
          <div className={styles.selectWrap}>
            <Dropdown
              id="role"
              label="User Role"
              options={roleOptions}
              value={newUser.role}
              placeholder="Select Role"
              onChange={(value) => handleSelectChange({ target: { name: 'role', value } })}
              triggerClassName={isErrorRole ? styles.triggerError : undefined}
            />
            {isErrorRole && (
              <span className={styles.fieldError}>{roleErrorMessage}</span>
            )}
          </div>
          <div className={styles.selectWrap}>
            <Dropdown
              id="branchId"
              label="Select Branch"
              options={branchOptions}
              value={newUser.branchId}
              placeholder="Select Branch"
              onChange={(value) => handleSelectChange({ target: { name: 'branchId', value } })}
              disabled={isBranchDisabled}
              triggerClassName={isErrorBranch ? styles.triggerError : undefined}
            />
            {isErrorBranch && (
              <span className={styles.fieldError}>{branchErrorMessage}</span>
            )}
          </div>
        </div>
        <div className={styles.divider} />
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>User Profile</h2>
          <div className={styles.fieldRow}>
            <div className={styles.fieldWrap}>
              <Input
                id="name"
                label="Name"
                value={newUser.name}
                onChange={(value) => handleChange({ target: { id: 'name', value } })}
                error={isErrorName ? nameErrorMessage : undefined}
              />
            </div>
            <div className={styles.fieldWrap}>
              <Input
                id="age"
                label="Age"
                value={newUser.age}
                onChange={(value) => handleChange({ target: { id: 'age', value } })}
                error={isErrorAge ? ageErrorMessage : undefined}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.selectWrap}>
              <Dropdown
                id="gender"
                label="Gender"
                options={genderOptions}
                value={newUser.gender}
                placeholder="Select Gender"
                onChange={(value) => handleSelectChange({ target: { name: 'gender', value } })}
                triggerClassName={isErrorGender ? styles.triggerError : undefined}
              />
              {isErrorGender && (
                <span className={styles.fieldError}>{genderErrorMessage}</span>
              )}
            </div>
            <div className={styles.fieldWrap}>
              <Input
                id="address"
                label="Address"
                value={newUser.address}
                onChange={(value) => handleChange({ target: { id: 'address', value } })}
                error={isErrorAddress ? addressErrorMessage : undefined}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldWrap}>
              <Input
                id="email"
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(value) => handleChange({ target: { id: 'email', value } })}
                error={isErrorEmail ? emailErrorMessage : undefined}
              />
            </div>
            <div className={styles.fieldWrap}>
              <Input
                id="phone"
                label="Phone"
                value={newUser.phone}
                onChange={(value) => handleChange({ target: { id: 'phone', value } })}
                error={isErrorPhone ? phoneErrorMessage : undefined}
              />
            </div>
          </div>
        </div>
        <div className={styles.divider} />
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>User Credentials</h2>
          <div className={styles.fieldRow}>
            <div className={styles.fieldWrap}>
              <Input
                id="username"
                label="Login User Name"
                value={newUser.username}
                onChange={(value) => handleChange({ target: { id: 'username', value } })}
                error={isErrorUserName ? userNameErrorMessage : undefined}
              />
            </div>
            <div className={styles.fieldWrap}>
              <Input
                id="password"
                label="Login Password"
                type="password"
                value={newUser.password}
                onChange={(value) => handleChange({ target: { id: 'password', value } })}
                error={isErrorPassword ? passwordErrorMessage : undefined}
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldWrap}>
              <Input
                id="pin"
                label="PIN"
                type="password"
                value={newUser.pin}
                onChange={(value) => handleChange({ target: { id: 'pin', value } })}
                error={isErrorPin ? pinErrorMessage : undefined}
                placeholder="6-digit PIN"
              />
            </div>
          </div>
          <div className={styles.actionsRow}>
            <Button
              type="button"
              className={styles.actionBtn}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Loading...' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className={styles.actionBtn}
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              type="button"
              variant="secondary"
              className={styles.actionBtn}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

AddNewUser.propTypes = {
  createUser: PropTypes.func.isRequired,
  findByUsername: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    branchList: PropTypes.array,
  }),
};

AddNewUser.defaultProps = {
  auth: {},
};

const mapStateToProps = (state) => ({
  auth: state.authState,
});

export default connect(mapStateToProps, {
  createUser,
  showAlert,
  findByUsername,
})(AddNewUser);
